import { mergeAttributes, Node, nodeInputRule } from "@tiptap/core";

export interface FigureOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    figure: {
      /**
       * Add a figure element
       */
      setFigure: (options: {
        src: string;
        alt?: string;
        caption?: string;
      }) => ReturnType;

      /**
       * Add a figure element at a specific position
       */
      setFigureAt: (options: {
        pos: number;
        src: string;
        alt?: string;
        caption?: string;
      }) => ReturnType;
    };
  }
}

export const inputRegex = /!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/;

export const Figure = Node.create<FigureOptions>({
  name: "figure",

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: "block",

  content: "inline*",

  defining: true,

  draggable: true,

  isolating: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) =>
          element.querySelector("img")?.getAttribute("src"),
      },

      alt: {
        default: null,
        parseHTML: (element) =>
          element.querySelector("img")?.getAttribute("alt"),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "figure",
        contentElement: "figcaption",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "figure",
      mergeAttributes(this.options.HTMLAttributes, {
        "data-type": this.name,
      }),
      [
        "img",
        mergeAttributes(HTMLAttributes, {
          draggable: false,
          contenteditable: false,
        }),
      ],
      ["figcaption", 0],
    ];
  },

  addCommands() {
    return {
      setFigure:
        ({ caption, ...attrs }) =>
        ({ chain }) => {
          return (
            chain()
              .insertContent({
                type: this.name,
                attrs,
                content: caption ? [{ type: "text", text: caption }] : [],
              })
              // set cursor at end of caption field
              .command(({ tr, commands }) => {
                const { doc, selection } = tr;

                // Safe cursor positioning with bounds checking
                if (selection.to >= 2) {
                  try {
                    const resolvedPos = doc.resolve(selection.to - 2);
                    const position = resolvedPos.end();
                    if (position >= 0 && position <= doc.content.size) {
                      return commands.setTextSelection(position);
                    }
                  } catch (error) {
                    // If resolving fails, fall back to simple positioning
                  }
                }

                // Fallback: position cursor at the end of selection
                const fallbackPos = Math.min(selection.to, doc.content.size);
                return commands.setTextSelection(fallbackPos);
              })
              .run()
          );
        },
      setFigureAt:
        ({ caption: _caption, ...attrs }) =>
        ({ chain }) => {
          return chain()
            .insertContentAt(attrs.pos, {
              type: "figure",
              attrs: { src: attrs.src },
            }) // set cursor at end of caption field
            .command(({ tr, commands }) => {
              const { doc, selection } = tr;

              // Find the inserted figure node
              let figurePos = -1;
              doc.descendants((node, pos) => {
                if (
                  node.type.name === "figure" &&
                  node.attrs.src === attrs.src
                ) {
                  figurePos = pos;
                  return false; // Stop traversal
                }
              });

              if (figurePos >= 0) {
                // Position cursor at the end of the figure's caption
                const figureNode = doc.nodeAt(figurePos);
                if (figureNode) {
                  const captionPos = figurePos + figureNode.nodeSize - 1;
                  if (captionPos >= 0 && captionPos <= doc.content.size) {
                    return commands.setTextSelection(captionPos);
                  }
                }
              }

              // Fallback: just position cursor after the selection
              const fallbackPos = Math.min(selection.to, doc.content.size);
              return commands.setTextSelection(fallbackPos);
            })
            .run();
        },
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: (match) => {
          const [, src, alt] = match;

          return { src, alt };
        },
      }),
    ];
  },
});
